'use client';
import { WebshopProductDetail } from '@cldsystems/ws-api-client';
import { Tab, Tabs } from '@heroui/react';
import { FC, useEffect, useState } from 'react';
import useCoreConfig from '@/hooks/use-core-config';
import useProductDetailPageContext, {
  useReviewProductIds,
} from '@/hooks/use-product-detail-page-context';
import { useTranslation } from '@/providers/app-provider/app-provider';
import DescriptionTab from './description-tab/description-tab';
import DetailsTab from './details-tab/details-tab';
import ReviewsTab from './reviews-tab/reviews-tab';

const ProductDetailTabs: FC = () => {
  const { t } = useTranslation();

  const { product } = useProductDetailPageContext();
  const reviewProductIds = useReviewProductIds();
  const { features } = useCoreConfig();

  const [resolvedDescription, setResolvedDescription] = useState<string>(product.description);
  type ProductWithRichTextFields = WebshopProductDetail & {
    descriptionUrl?: string;
    secondaryDescription?: string | null;
  };
  const productWithRichTextFields = product as ProductWithRichTextFields;
  const descriptionUrl = productWithRichTextFields.descriptionUrl;

  useEffect(() => {
    let isCancelled = false;
    const load = async () => {
      if (descriptionUrl) {
        try {
          const res = await fetch(descriptionUrl, { cache: 'no-store' });
          if (!res.ok) throw new Error(`Failed to fetch description (${res.status})`);
          const data = (await res.json()) as { description?: string };
          if (!isCancelled) setResolvedDescription(data?.description ?? product.description);
        } catch {
          if (!isCancelled) setResolvedDescription(product.description);
        }
      } else {
        setResolvedDescription(product.description);
      }
    };
    load();
    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptionUrl, product.description]);

  return (
    <div className="flex w-full flex-col">
      <Tabs
        classNames={{
          cursor: 'w-full',
          tabList: 'flex flex-row gap-8 mb-2',
          tab: 'text-medium font-medium rounded-none',
          panel: 'bg-transparent p-0 shadow-none',
        }}
        variant="solid"
      >
        <Tab key="description" title={t?.productDetail.description}>
          <DescriptionTab description={resolvedDescription} />
        </Tab>
        <Tab key="details" title={t?.productDetail.details}>
          <DetailsTab
            secondaryDescription={productWithRichTextFields.secondaryDescription}
            productAttributeGroups={product.productAttributeGroups}
            categoryAttributeGroups={product.categoryAttributeGroups}
          />
        </Tab>
        {features.productDetailReviews && (
          <Tab key="reviews" title={t?.productDetail.reviews}>
            <ReviewsTab productIds={reviewProductIds} />
          </Tab>
        )}
      </Tabs>
    </div>
  );
};

export default ProductDetailTabs;
